import { useState } from 'react'
import { MenuItem } from '../Menus'
import Button from '../../../components/common/Button'

interface MenuListProps {
  menus: MenuItem[]
  onEdit: (menu: MenuItem) => void
  onToggleVisibility: (menu: MenuItem) => void
  onReorder: (reorderedMenus: MenuItem[]) => void
}

function MenuList({ menus, onEdit, onToggleVisibility, onReorder }: MenuListProps) {
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null)
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null)

  const handleDragStart = (e: React.DragEvent, index: number) => {
    setDraggedIndex(index)
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('text/html', e.currentTarget.innerHTML)
  }

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    setDragOverIndex(index)
  }

  const handleDragLeave = () => {
    setDragOverIndex(null)
  }

  const handleDrop = (e: React.DragEvent, dropIndex: number) => {
    e.preventDefault()

    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null)
      setDragOverIndex(null)
      return
    }

    const newMenus = [...menus]
    const draggedMenu = newMenus[draggedIndex]

    // Remove from old position
    newMenus.splice(draggedIndex, 1)

    // Insert at new position
    newMenus.splice(dropIndex, 0, draggedMenu)

    onReorder(newMenus)
    setDraggedIndex(null)
    setDragOverIndex(null)
  }

  const handleDragEnd = () => {
    setDraggedIndex(null)
    setDragOverIndex(null)
  }

  const handleMoveUp = (index: number) => {
    if (index === 0) return
    const newMenus = [...menus]
    const temp = newMenus[index]
    newMenus[index] = newMenus[index - 1]
    newMenus[index - 1] = temp
    onReorder(newMenus)
  }

  const handleMoveDown = (index: number) => {
    if (index === menus.length - 1) return
    const newMenus = [...menus]
    const temp = newMenus[index]
    newMenus[index] = newMenus[index + 1]
    newMenus[index + 1] = temp
    onReorder(newMenus)
  }

  if (menus.length === 0) {
    return (
      <div className="table-container">
        <p className="text-center" style={{ padding: '2rem' }}>
          Aucun menu disponible
        </p>
      </div>
    )
  }

  return (
    <div className="table-container">
      <table className="table menus-table">
        <thead>
          <tr>
            <th style={{ width: '60px' }}>Ordre</th>
            <th>Libellé</th>
            <th>Slug</th>
            <th style={{ width: '120px' }}>Visibilité</th>
            <th style={{ width: '200px' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {menus.map((menu, index) => (
            <tr
              key={menu.id}
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, index)}
              onDragEnd={handleDragEnd}
              className={`
                menu-row
                ${draggedIndex === index ? 'dragging' : ''}
                ${dragOverIndex === index ? 'drag-over' : ''}
              `.trim()}
            >
              <td className="menu-position">
                <div className="drag-handle" title="Glisser pour réorganiser">
                  ⋮⋮
                </div>
                <span className="position-number">{index + 1}</span>
              </td>
              <td>
                <strong>{menu.label}</strong>
              </td>
              <td>
                <code className="menu-slug">/{menu.slug}</code>
              </td>
              <td>
                <button
                  onClick={() => onToggleVisibility(menu)}
                  className={`visibility-badge ${menu.is_visible ? 'visible' : 'hidden'}`}
                  title={menu.is_visible ? 'Cliquer pour masquer' : 'Cliquer pour afficher'}
                >
                  {menu.is_visible ? '👁 Visible' : '🚫 Masqué'}
                </button>
              </td>
              <td>
                <div className="table-actions">
                  <Button
                    variant="outline"
                    size="small"
                    onClick={() => handleMoveUp(index)}
                    disabled={index === 0}
                    title="Déplacer vers le haut"
                  >
                    ↑
                  </Button>
                  <Button
                    variant="outline"
                    size="small"
                    onClick={() => handleMoveDown(index)}
                    disabled={index === menus.length - 1}
                    title="Déplacer vers le bas"
                  >
                    ↓
                  </Button>
                  <Button
                    variant="primary"
                    size="small"
                    onClick={() => onEdit(menu)}
                  >
                    Modifier
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

export default MenuList
